import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes, FaBus, FaClock } from 'react-icons/fa';
import BookingForm from '../../components/common/BookingForm';
import { companyInfo } from '../../data/tourData';
import './RudranathTour.css';

const RudranathTour = () => {
  return (
    <>
      <Helmet>
        <title>Rudranath Tour - NomadTrailz</title>
        <meta name="description" content="Experience the sacred Rudranath pilgrimage with NomadTrailz. Trek through beautiful Himalayan landscapes to reach the ancient Shiva temple." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1754934085/dukeakdjx7p3slfkl2km.webp" 
              alt="Rudranath" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Rudranath Tour
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Sacred pilgrimage to Rudranath temple through beautiful Himalayan landscapes
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Rudranath is one of the five sacred temples of Panch Kedar, dedicated to Lord Shiva. 
                    This spiritual journey takes you through pristine Himalayan landscapes, dense forests, 
                    and alpine meadows to reach the ancient temple where Lord Shiva's face is worshipped. 
                    Experience the divine energy and breathtaking natural beauty of the Garhwal Himalayas.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Sacred Rudranath temple darshan</li>
                    <li>Trek through Lyiti Bugyal meadows</li>
                    <li>Panoramic Himalayan views</li>
                    <li>Evening aarti ceremony</li>
                    <li>Kalpeshwar temple visit</li>
                    <li>Spiritual experience in nature</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Delhi to Sagar Village</h4>
                    <ul>
                      <li>Depart from Delhi around 11:00 PM in a luxury traveller</li>
                      <li>Overnight journey to Rishikesh</li>
                      <li>Morning arrival in Rishikesh</li>
                      <li>Travel from Rishikesh to Sagar Village (8-10 hours by road)</li>
                      <li>Evening: Check into guest house or homestay</li>
                      <li>Dinner and rest for overnight</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Sagar Village to Lyiti Bugyal</h4>
                    <ul>
                      <li>Early morning breakfast at hotel</li>
                      <li>Start trek from Sagar Village to Lyiti Bugyal (10-12 km)</li>
                      <li>Trek through lush forests and scenic views</li>
                      <li>Arrive at Lyiti Bugyal in the evening</li>
                      <li>Set up camp or stay in basic lodges</li>
                      <li>Enjoy panoramic view and rest overnight</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Lyiti Bugyal to Rudranath</h4>
                    <ul>
                      <li>Early morning continue trek to Pitradhar</li>
                      <li>Continue to Rudranath (8-10 km)</li>
                      <li>Reach Rudranath temple dedicated to Lord Shiva</li>
                      <li>Evening: Attend aarti ceremony at Rudranath temple</li>
                      <li>Stay overnight in basic lodge or camp near temple</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Rudranath to Sagar Village</h4>
                    <ul>
                      <li>Morning darshan at Rudranath temple</li>
                      <li>Start trekking back to Panar Bugyal</li>
                      <li>Continue trekking from Panar Bugyal to Sagar Village</li>
                      <li>Arrive at Sagar Village and rest overnight</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Sagar Village to Kalpeshwar and back to Delhi</h4>
                    <ul>
                      <li>Travel back from Sagar Village to Urgam Village</li>
                      <li>Darshan at Kalpeshwar Temple</li>
                      <li>4 PM: Get back to traveller and drive back to Delhi</li>
                      <li>Late night arrival in Delhi</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />Guest house or homestay accommodation in Sagar Village (1 night)</li>
                    <li><FaCheck className="check-icon" />Tented camp or basic lodge accommodation at Lyiti Bugyal (1 night)</li>
                    <li><FaCheck className="check-icon" />Tented camp or basic lodge accommodation near Rudranath Temple (1 night)</li>
                    <li><FaCheck className="check-icon" />Guest house or homestay accommodation in Sagar Village (1 night)</li>
                    <li><FaCheck className="check-icon" />Breakfast and Dinner included. Vegetarian meals (Evening Tea)</li>
                    <li><FaCheck className="check-icon" />Transfer from Delhi to Sagar Village and back in private vehicle</li>
                    <li><FaCheck className="check-icon" />Experienced and qualified trekking guide</li>
                    <li><FaCheck className="check-icon" />Camping equipment (tents, sleeping bags, mattresses)</li>
                    <li><FaCheck className="check-icon" />24/7 customer support</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />Personal Expenses: Any Trek Fee (₹200 For Non Student) (₹100 for Student)</li>
                    <li><FaTimes className="times-icon" />Personal trekking gear (clothing, shoes, etc.)</li>
                    <li><FaTimes className="times-icon" />Personal expenses such as tips, laundry, telephone calls, and beverages</li>
                    <li><FaTimes className="times-icon" />Travel insurance (highly recommended)</li>
                    <li><FaTimes className="times-icon" />Transport: Airfare or train fare to and from Rishikesh/Haridwar</li>
                    <li><FaTimes className="times-icon" />Any kind of transportation not mentioned in the itinerary</li>
                    <li><FaTimes className="times-icon" />Additional Services: Any additional accommodation or meals beyond the specified itinerary</li>
                    <li><FaTimes className="times-icon" />Any costs arising due to unforeseen circumstances (weather conditions, roadblocks, natural calamities)</li>
                    <li><FaTimes className="times-icon" />Miscellaneous: Any other item not mentioned in the inclusions section</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 8,500</span>
                    <span className="duration">5 Days / 4 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Rudranath, Uttarakhand</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: May to October</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 10-15 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Moderate</span>
                    </div>
                  </div>

                  <div className="pricing-options">
                    <h4>Cost Per Person</h4>
                    <div className="price-option">
                      <span className="option">QUAD SHARE</span>
                      <span className="amount">₹ 8,500 + 5% GST</span>
                    </div>
                    <div className="price-option">
                      <span className="option">TRIPLE SHARE</span>
                      <span className="amount">₹ 9,500 + 5% GST</span>
                    </div>
                    <div className="price-option">
                      <span className="option">DOUBLE SHARE</span>
                      <span className="amount">₹ 10,500 + 5% GST</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="tour-logistics-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3>Tour Logistics</h3>
                  <div className="logistics-grid">
                    <div className="logistics-item">
                      <FaBus className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Point:</span>
                        <span className="logistics-value">Delhi: ISBT Kashmere Gate</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaClock className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Time:</span>
                        <span className="logistics-value">9:00 PM</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Rudranath Sacred Trek"
                  tourPrice="₹ 8,500"
                  tourDuration="5 Days / 4 Nights"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default RudranathTour;
